/**
 * An event of a kind Setu does not render.
 *
 * This is the row that makes "we are not building that" a defensible position
 * rather than a hole. Setu declines a dozen vertical apps — git, calendars, wiki,
 * marketplaces — and their events still arrive: quoted by someone, or referenced in
 * a thread the reader is in. The three options for such an event are to drop it, to
 * render it as a blank card, or to say what it is and offer to open it somewhere
 * that understands it.
 *
 * Dropping it is the worst: a reader who was mentioned in something never learns it
 * exists. A blank card is nearly as bad, because it reads as a bug in Setu rather
 * than as a kind Setu does not handle. So: the author's own NIP-31 `alt` line, and a
 * NIP-89 menu when anyone has recommended a handler.
 *
 * Two rules about the wording:
 *
 *  - **The kind number is always shown**, even when there is an `alt`. The `alt` is
 *    the author's description and can say anything; the number is checkable, and it
 *    is what makes the row's own claim ("Setu does not render this") verifiable
 *    rather than a shrug.
 *  - **The handler list is never presented as a recommendation of Setu's.** These
 *    are third-party web apps named by strangers. Each entry shows the host it will
 *    send the reader to, opens in a new tab, and carries `rel="noopener noreferrer"`
 *    — a handler must not get a window handle back into a tab holding a signing key.
 */

import { altText, encodeNevent, type NostrEvent } from "@setu/protocol";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@setu/ui";
import { ExternalLink, HelpCircle } from "lucide-react";
import { useMemo } from "react";
import { useAppHandlers } from "./useAppHandlers";

/** How much of a stranger's `alt` line to render. */
const MAX_ALT = 280;

export function UnhandledKind({
  event,
  relayHint,
  className,
}: {
  event: NostrEvent;
  /** A relay we saw it on, so a handler can actually resolve the reference. */
  relayHint?: string;
  className?: string;
}) {
  const bech32 = useMemo(
    () =>
      encodeNevent({
        id: event.id,
        author: event.pubkey,
        kind: event.kind,
        ...(relayHint ? { relays: [relayHint] } : {}),
      }),
    [event.id, event.pubkey, event.kind, relayHint],
  );
  const handlers = useAppHandlers(event.kind, bech32);

  const described = altText(event);
  const alt =
    described !== undefined && described.length > MAX_ALT
      ? `${described.slice(0, MAX_ALT)}…`
      : described;

  return (
    <div className={cn("mt-1 flex flex-col gap-1.5", className)}>
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <HelpCircle aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        <span className="min-w-0">
          {alt ?? "Setu does not render this kind of event."}{" "}
          <span className="setu-mono whitespace-nowrap">
            (kind {event.kind})
          </span>
        </span>
      </p>

      {handlers.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="self-start">
              <ExternalLink />
              Open in…
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-w-xs">
            {handlers.map((option) => (
              <DropdownMenuItem key={option.handler.address} asChild>
                <a
                  href={option.url}
                  target="_blank"
                  // A handler must not get a handle back into this tab.
                  rel="noopener noreferrer"
                  className="flex flex-col items-start gap-0"
                >
                  <span>{option.handler.name ?? option.host}</span>
                  {/* The host, always. The name is a stranger's string and could
                      claim to be anything; the host is where the reader is going. */}
                  <span className="setu-mono text-2xs text-muted-foreground">
                    {option.host}
                  </span>
                </a>
              </DropdownMenuItem>
            ))}
            <p className="px-2 py-1.5 text-2xs text-muted-foreground">
              Suggested by other Nostr accounts, not by Setu. These are external
              sites.
            </p>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
